/**
 * PortalOutcomes — Revenue & decision outcomes dashboard
 *
 * Shows real data from scoring_results + outcomes tables:
 * - Decision distribution (CALL / LEAD / MULTI_OFFER pie chart)
 * - Revenue by decision type
 * - Score distributions
 * - Recent decisions with scores
 */

import React, { useState, useEffect, useCallback } from 'react';
import PortalLayout from '@/components/portal/PortalLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid,
} from 'recharts';
import { DollarSign, Phone, Mail, Layers, TrendingUp, Activity, Loader2, Wifi } from 'lucide-react';
import { supabaseGet } from '@/utils/supabaseRest';
import { useRealtimeScoring } from '@/hooks/useRealtimeScoring';

interface ScoringResult {
  id: string;
  session_id: string;
  intent_score: number;
  monetization_score: number;
  decision: string;
  vertical_id: string | null;
  computed_at: string;
}

interface Outcome {
  id: string;
  session_id: string;
  decision: string;
  vertical_id: string | null;
  revenue: number;
  outcome_status: string;
  created_at: string;
}

const COLORS = { CALL: '#10b981', LEAD: '#3b82f6', MULTI_OFFER: '#8b5cf6' };

export default function PortalOutcomes() {
  const [results, setResults] = useState<ScoringResult[]>([]);
  const [outcomes, setOutcomes] = useState<Outcome[]>([]);
  const [loading, setLoading] = useState(true);
  const { events: liveEvents, connected: realtimeConnected } = useRealtimeScoring();

  // Merge live events into results (prepend, deduplicate by id)
  useEffect(() => {
    if (liveEvents.length > 0) {
      setResults(prev => {
        const existingIds = new Set(prev.map(r => r.id));
        const newOnes = liveEvents.filter(e => !existingIds.has(e.id));
        return [...newOnes.map(e => ({ ...e, intent_score: Number(e.intent_score), monetization_score: Number(e.monetization_score) })), ...prev];
      });
    }
  }, [liveEvents]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [r, o] = await Promise.all([
        supabaseGet('scoring_results?order=computed_at.desc&limit=500'),
        supabaseGet('outcomes?order=created_at.desc&limit=500'),
      ]);
      setResults(r || []);
      setOutcomes(o || []);
    } catch (err) {
      console.error('Failed to load outcomes data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Decision distribution
  const decisionCounts = results.reduce<Record<string, number>>((acc, r) => {
    acc[r.decision] = (acc[r.decision] || 0) + 1;
    return acc;
  }, {});
  const pieData = Object.entries(decisionCounts).map(([name, value]) => ({ name, value }));

  // Revenue by decision
  const revenueByDecision = outcomes.reduce<Record<string, number>>((acc, o) => {
    acc[o.decision] = (acc[o.decision] || 0) + Number(o.revenue || 0);
    return acc;
  }, {});
  const revenueData = Object.entries(revenueByDecision).map(([name, revenue]) => ({ name, revenue: Math.round(revenue * 100) / 100 }));

  // Avg scores by decision
  const scoresByDecision = results.reduce<Record<string, { intent: number[]; monetization: number[] }>>((acc, r) => {
    if (!acc[r.decision]) acc[r.decision] = { intent: [], monetization: [] };
    acc[r.decision].intent.push(Number(r.intent_score));
    acc[r.decision].monetization.push(Number(r.monetization_score));
    return acc;
  }, {});
  const avgScoreData = Object.entries(scoresByDecision).map(([decision, s]) => ({
    decision,
    avg_intent: Math.round(s.intent.reduce((a, b) => a + b, 0) / s.intent.length),
    avg_monetization: Math.round(s.monetization.reduce((a, b) => a + b, 0) / s.monetization.length),
  }));

  const totalRevenue = outcomes.reduce((sum, o) => sum + Number(o.revenue || 0), 0);
  const totalDecisions = results.length;
  const conversionRate = outcomes.filter(o => o.outcome_status === 'converted').length / (totalDecisions || 1) * 100;

  return (
    <PortalLayout>
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Activity className="h-6 w-6 text-emerald-400" />
            Outcomes & Revenue
          </h1>
          <div className="flex items-center gap-3 mt-1">
            <p className="text-sm text-slate-400">
              Scoring decisions, routing outcomes, and revenue performance.
            </p>
            {realtimeConnected && (
              <span className="flex items-center gap-1 text-[10px] text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full">
                <Wifi className="h-3 w-3" />
                Live
              </span>
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 text-emerald-400 animate-spin" />
          </div>
        ) : (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <KpiCard label="Total Decisions" value={totalDecisions.toLocaleString()} icon={<TrendingUp className="h-5 w-5" />} color="text-emerald-400" />
              <KpiCard label="Total Revenue" value={`$${totalRevenue.toFixed(2)}`} icon={<DollarSign className="h-5 w-5" />} color="text-amber-400" />
              <KpiCard label="Conversion Rate" value={`${conversionRate.toFixed(1)}%`} icon={<Activity className="h-5 w-5" />} color="text-blue-400" />
              <KpiCard label="Avg Revenue/Decision" value={`$${totalDecisions ? (totalRevenue / totalDecisions).toFixed(2) : '0.00'}`} icon={<DollarSign className="h-5 w-5" />} color="text-violet-400" />
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Decision Distribution */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-slate-300">Decision Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  {pieData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                          {pieData.map((entry) => (
                            <Cell key={entry.name} fill={COLORS[entry.name as keyof typeof COLORS] || '#6b7280'} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[220px] flex items-center justify-center text-sm text-slate-500">No data yet</div>
                  )}
                </CardContent>
              </Card>

              {/* Avg Scores by Decision */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-slate-300">Avg Scores by Decision</CardTitle>
                </CardHeader>
                <CardContent>
                  {avgScoreData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={avgScoreData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis dataKey="decision" tick={{ fill: '#94a3b8', fontSize: 12 }} />
                        <YAxis domain={[0, 100]} tick={{ fill: '#94a3b8', fontSize: 12 }} />
                        <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8 }} />
                        <Bar dataKey="avg_intent" fill="#10b981" name="Intent" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="avg_monetization" fill="#8b5cf6" name="Monetization" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[220px] flex items-center justify-center text-sm text-slate-500">No data yet</div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Recent Decisions Table */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-slate-300">Recent Decisions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-slate-500 border-b border-slate-700">
                        <th className="text-left py-2 px-2">Session</th>
                        <th className="text-center py-2 px-2">Intent</th>
                        <th className="text-center py-2 px-2">Monetization</th>
                        <th className="text-center py-2 px-2">Decision</th>
                        <th className="text-left py-2 px-2">Vertical</th>
                        <th className="text-right py-2 px-2">Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.slice(0, 20).map((r) => (
                        <tr key={r.id} className="border-b border-slate-800 hover:bg-slate-800/50">
                          <td className="py-2 px-2 text-slate-400 font-mono text-xs">{r.session_id.slice(0, 12)}...</td>
                          <td className="py-2 px-2 text-center">
                            <span className={`font-bold ${Number(r.intent_score) >= 70 ? 'text-emerald-400' : Number(r.intent_score) >= 40 ? 'text-blue-400' : 'text-slate-400'}`}>
                              {Number(r.intent_score)}
                            </span>
                          </td>
                          <td className="py-2 px-2 text-center">
                            <span className={`font-bold ${Number(r.monetization_score) >= 60 ? 'text-violet-400' : 'text-slate-400'}`}>
                              {Number(r.monetization_score)}
                            </span>
                          </td>
                          <td className="py-2 px-2 text-center">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              r.decision === 'CALL' ? 'bg-emerald-500/20 text-emerald-400' :
                              r.decision === 'LEAD' ? 'bg-blue-500/20 text-blue-400' :
                              'bg-violet-500/20 text-violet-400'
                            }`}>{r.decision}</span>
                          </td>
                          <td className="py-2 px-2 text-slate-300 text-xs">{r.vertical_id?.replace(/_/g, ' ') || '—'}</td>
                          <td className="py-2 px-2 text-right text-slate-500 text-xs">
                            {new Date(r.computed_at).toLocaleTimeString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {results.length === 0 && (
                    <div className="text-center py-8 text-sm text-slate-500">No scoring decisions yet. Decisions appear here as users interact with the widget.</div>
                  )}
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </PortalLayout>
  );
}

function KpiCard({ label, value, icon, color }: { label: string; value: string; icon: React.ReactNode; color: string }) {
  return (
    <Card className="bg-slate-800/50 border-slate-700">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-slate-500">{label}</span>
          <span className={color}>{icon}</span>
        </div>
        <div className="text-xl font-bold text-white">{value}</div>
      </CardContent>
    </Card>
  );
}
